import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/chart/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/chart/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/chart/FETCH_FAILURE'

export type ChartModuleState = {
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
}

// Initial State
const initialState: ChartModuleState = {
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function loadChartData(endpoint: string, options: EntityOptions = {}) {
  const { chart_data_key } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<object[]>> => {
    const loadedForKeys = [...getState().charts.loadedForKeys]

    if (chart_data_key && !loadedForKeys.includes(chart_data_key)){
      loadedForKeys.push(chart_data_key)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(endpoint, options)
      .then(({ data }): { success: true, data: object } => {
        const normalizedJson = normalize(data.data, Schemas.CHART_DATA)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data): { success: false, errors: string[] } => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ChartModuleState = initialState,
  action: Action = { type: '' },
): ChartModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
